import { ISiteViewModel } from "@/Administration/Models/UI/Sites/ISiteViewModel";
import { Client } from "@/Common/Api/Client";
import { Location } from "@/Common/Api/Dsq/Dto/Responses/MyleDSQResultsResponse";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DSQLocationTypeEnum } from "@/Common/Models/DSQ/DSQLocationTypeEnum";
import { Site } from "@/Common/Models/Sites/ResponseModels/Site";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import AddressSection from "./SiteView/AddressSection";
import CostCenterSection from "./SiteView/CostCenterSection";
import EmailSection from "./SiteView/EmailSection";
import HeaderSection from "./SiteView/HeaderSection";
import InfoInPortalSection from "./SiteView/InfoInPortalSection";
import PhoneSection from "./SiteView/PhoneSection";
import SecuritySection from "./SiteView/SecuritySection";
import SiteInfoSection from "./SiteView/SiteInfoSection";
import TaxesSection from "./SiteView/TaxesSection";

interface ISiteViewProps {
    model: ISiteViewModel;
}

class SiteViewState {
    public site: Site;
    public currentlySelectedDSQSite: Location;
    public dsqLocationName: string;
    public selectedDSQLocationType: string;
    public displaySiteSearchPromptPopup: boolean;
    public displayDSQLocationSearchResultsPopup: boolean;
    public displayDSQLocationDetailsPopup: boolean;
    public dsqLocations: Location[];
    public costCenters: PrivateBillCostCenter[];
}

class SiteView extends React.Component<ISiteViewProps, SiteViewState> {
    constructor(props: ISiteViewProps) {
        super(props);

        const initialState = new SiteViewState();

        initialState.displaySiteSearchPromptPopup = false;
        initialState.dsqLocationName = "";
        initialState.selectedDSQLocationType = "0";
        initialState.displayDSQLocationSearchResultsPopup = false;
        initialState.site = MyleHelper.CloneObject(props.model.get("Model"));
        initialState.costCenters = props.model.get("CostCenters").toArray();

        this.state = initialState;
    }

    public componentDidUpdate(prevProps: ISiteViewProps): void {
        if (this.props.model.get("Model") !== prevProps.model.get("Model")) {
            this.mutateState(s => {
                s.site = MyleHelper.CloneObject(this.props.model.get("Model"));
                s.costCenters = this.props.model.get("CostCenters").toArray();
            });
        }
    }

    public render(): JSX.Element {
        const enableSitePortalInformation = cacheStore.ClinicSettings.EnableSitePortalInformation;
        const searchLocationPopup: JSX.Element = (
            <Popup Id="dsqLocation" title="Sites DSQ" onCloseDelegate={() => this.mutateState(s => s.displaySiteSearchPromptPopup = false)} draggable={false} cssClass="dsq-site-popup">
                <div className="p-body">
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("NameLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-9 col-sm-12 ">
                            <TextboxControl
                                debounceMs={400}
                                value={this.state.dsqLocationName}
                                onChange={(text: string) => this.mutateState(s => s.dsqLocationName = text)}
                                style={{ marginRight: "10px" }}
                                className="form-control primary-search"
                            />
                        </div>
                    </div>
                    <div className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-3 col-sm-12 vcenter text-right">
                            <label className="form-label">{cacheStore.GetString("TypeLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-9 col-sm-12 ">
                            <Combobox
                                HasEmptyItem={false}
                                id="_DSQLocationTypeComboBox"
                                items={[
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.HOSP.toString(), "Centre hospitalier", 0),
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.CHR.toString(), "Centre d'hÃ©bergement et de soins de longue durÃ©e", 1),
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.OF.toString(), "Clinique", 2),
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.CCC.toString(), "Centre local de services communautaires", 3),
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.RH.toString(), "Centre de rÃ©adaptation", 4),
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.LSIND.toString(), "Lieu pour services indirects", 4),
                                    new ComboBoxDropDownItem(DSQLocationTypeEnum.PHARM.toString(), "Pharmacie", 4)
                                ].toImmutable()}
                                selectedCode={this.state.selectedDSQLocationType}
                                style={{ width: "350px" }}
                                onChange={(value: string) => {
                                    this.mutateState(s => {
                                        s.selectedDSQLocationType = value;
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            className="btn btn-default"
                            disabled={false}
                            onClick={() => this.mutateState(s => s.displaySiteSearchPromptPopup = false)}
                            label={cacheStore.GetString("CloseLabel")}
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            disabled={false}
                            onClick={() => this.onSearchDSQLocation()}
                            label={cacheStore.GetString("Rechercher Site")}
                        />
                    </ButtonGroup>
                </div>
            </Popup>
        );

        const searchLocationsResultPopup: JSX.Element = (
            <Popup Id="dsqLocation" title="Sites DSQ correspondants" onCloseDelegate={() => this.mutateState(s => s.displayDSQLocationSearchResultsPopup = false)} draggable={false} cssClass="dsq-site-popup">

                <div className="p-body" style={{ maxHeight: "550px", overflow: "auto" }}>
                    {this.state.dsqLocations != null && this.state.dsqLocations.length > 0
                        && this.state.dsqLocations.map((loc, i) => {
                            let addressDetails = "";

                            if (loc.Address != null) addressDetails += loc.Address + " ";
                            if (loc.City != null) addressDetails += loc.City + " ";
                            if (loc.ZipCode != null) addressDetails += loc.ZipCode + " ";

                            return (
                                <div key={"location_" + i} className="DSQ-location-search-item" style={{}}>
                                    <div style={{ float: "left", width: "80%" }}>
                                        {loc.Name != null && <p>{loc.Name}</p>}
                                        <p>{addressDetails}</p>

                                    </div>
                                    <a style={{ float: "right" }} onClick={() => this.getLocationDetails(loc)}>(Voir les dÃ©tails)</a>
                                </div>
                            );
                        })
                    }

                </div>
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                            <ButtonControl
                                label={cacheStore.GetString("CloseLabel")}
                                className="btn btn-default"
                                onClick={() => this.mutateState(s => s.displayDSQLocationSearchResultsPopup = false)}
                            />
                        </div>
                    </div>
                </div>
            </Popup>
        );

        const locationDetailsPopup: JSX.Element = (
            <Popup Id="dsqLocation" title="Details du site" onCloseDelegate={() => this.mutateState(s => s.displayDSQLocationDetailsPopup = false)} draggable={false} cssClass="dsq-site-popup">

                <div className="p-body">

                    {this.state != null && this.state.currentlySelectedDSQSite && (
                        <div>
                            <p>{this.state.currentlySelectedDSQSite.Name}</p>
                            <p>{this.state.currentlySelectedDSQSite.Address + " " + this.state.currentlySelectedDSQSite.City + " " + this.state.currentlySelectedDSQSite.ZipCode}</p>
                            <p>{this.state.currentlySelectedDSQSite.Phone}</p>
                            {this.state.currentlySelectedDSQSite.AdditionalLocator != null && <p>{this.state.currentlySelectedDSQSite.AdditionalLocator}</p>}
                        </div>
                    )}

                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonControl
                        className="btn btn-primary"
                        disabled={false}
                        onClick={() => this.syncDSQLocation()}
                        label={cacheStore.GetString("btnSyncDSQShort")}
                    />
                    <ButtonControl
                        label={cacheStore.GetString("CloseLabel")}
                        className="btn btn-default"
                        onClick={() => this.mutateState(s => s.displayDSQLocationDetailsPopup = false)}
                    />
                </div>
            </Popup>
        );

        const showMultifactorAuthenticationNumberBadAccess = this.state.site.MultifactorAuthenticationNumberBadAccess > 0;

        return (
            <div>
                <div className="fxheight">
                    {!IsNullOrWhiteSpace(this.state.site.ParentSiteId) && <HeaderSection site={this.state.site} />}
                    <div className="fxheight scrollable" data-hvar={60}>
                        <SiteInfoSection site={this.state.site} />
                        {!IsNullOrWhiteSpace(this.state.site.NameDsq) && this.dsqInfoSection(this.state.site)}
                        {!IsNullOrWhiteSpace(this.state.site.ParentSiteId) && (
                            <div>
                                {this.state.site.Adresses && this.state.site.Adresses.length > 0 && <AddressSection addresses={this.state.site.Adresses} />}
                                {this.state.site.Phones && this.state.site.Phones.length > 0 && <PhoneSection phones={this.state.site.Phones} />}
                                {this.state.site.Emails && this.state.site.Emails.length > 0 && <EmailSection emails={this.state.site.Emails} />}
                                {enableSitePortalInformation && (
                                    <InfoInPortalSection
                                        localizedDisplayName={this.state.site.LocalizedDisplayName}
                                        specialties={this.state.site.Specialties}
                                        offeredSpecialtyIds={this.state.site.OfferedSpecialtyIds}
                                        openingHours={this.state.site.OpeningHours}
                                        localizedOpeningHoursNote={this.state.site.LocalizedOpeningHoursNote}
                                        localizedWebsite={this.state.site.LocalizedWebsite}
                                        localizedDescription={this.state.site.LocalizedDescription}
                                    />
                                )}
                                {(this.state.site.NumberBadAccess > 0 || showMultifactorAuthenticationNumberBadAccess || this.state.site.PeriodOfInactivityDays > 0) && (
                                    <SecuritySection
                                        numberBadAccess={this.state.site.NumberBadAccess}
                                        multifactorAuthenticationNumberBadAccess={this.state.site.MultifactorAuthenticationNumberBadAccess}
                                        periodOfInactivityDays={this.state.site.PeriodOfInactivityDays}
                                    />
                                )}
                                <TaxesSection gst={this.state.site.GstValue} qst={this.state.site.QstValue} hst={this.state.site.HstValue} genericTaxValue={this.state.site.GenericTaxValue} site={new SitesModel(this.state.site)} />
                                {this.state.costCenters && this.state.costCenters.length > 0 && <CostCenterSection costCenters={this.state.costCenters} />}
                            </div>
                        )}
                    </div>
                    <div className="meta-footer">
                        <div className="row">
                            <div className="col-lg-4 col-md-3 col-sm-3">
                                {sessionStore.isMsaActive && (
                                    <ButtonControl
                                        className="btn btn-primary"
                                        onClick={() => this.mutateState(s => s.displaySiteSearchPromptPopup = true)}
                                        label="Synchronize with DSQ"
                                    />
                                )}
                            </div>
                            <div className="text-right push-right-15">
                                <ButtonControl
                                    className="btn btn-primary"
                                    onClick={() => this.onModifyClick()}
                                    label={cacheStore.GetString("ModifyLabel")}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {this.state != null && this.state.displaySiteSearchPromptPopup === true
                    && searchLocationPopup
                }

                {this.state != null && this.state.dsqLocations != null && this.state.displayDSQLocationSearchResultsPopup === true
                    && searchLocationsResultPopup
                }

                {this.state != null && this.state.currentlySelectedDSQSite != null && this.state.displayDSQLocationDetailsPopup === true
                    && locationDetailsPopup
                }
            </div>

        );
    }

    private dsqInfoSection(site: Site): JSX.Element {
        return (
            <div>
                <div className="view-section-header">
                    <h3 className="view-section-title">{cacheStore.GetString("DSQLabel")}</h3>
                </div>
                <div className="view-section-body">
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("NameLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.NameDsq}</strong>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("StreetAddressLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.AddressDsq}</strong>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("CityLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.CityDsq}</strong>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("ZipPostalCodeLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.PostalCodeDsq}</strong>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("PhoneLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.PhoneDsq}</strong>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private onModifyClick(): void {
        sessionStore.dispatch(LocalOperations.Sites.ModifySite);
    }

    private mutateState(mutation: (s: SiteViewState) => void, then: () => void = null): void {
        const s = new SiteViewState();
        mutation(s);
        this.setState(s, then);
    }

    private onSearchDSQLocation(): void {
        const req = {
            LocationName: this.state.dsqLocationName,
            LocationType: this.state.selectedDSQLocationType
        };

        Client.PostDSQ(ServerOperations.Server.DSQ_GetLocations, req, response => {
            if (response.Errors != null && response.Errors.length > 0) {
                sessionStore.showPopup(response.Errors);
                this.mutateState(s => {
                    s.displayDSQLocationSearchResultsPopup = false;
                    s.displaySiteSearchPromptPopup = false;
                });
            }
            else {
                let dsqLocations: Location[] = [];

                if (response.LocationsFound != null && response.LocationsFound.length > 0) {
                    dsqLocations = response.LocationsFound;
                }

                this.mutateState(s => {
                    s.dsqLocations = dsqLocations;
                    s.displayDSQLocationSearchResultsPopup = true;
                    s.displaySiteSearchPromptPopup = false;
                });
            }
        }, error => {
            console.log(error);
        });
    }

    private getLocationDetails(x: Location): void {
        const req = {
            LocationNUI: x.NUI
        };

        Client.PostDSQ(ServerOperations.Server.DSQ_GetLocationDetails, req, response => {
            if (response.Errors != null && response.Errors.length > 0) {
                sessionStore.showPopup(response.Errors);
            }
            else {
                this.mutateState(s => {
                    s.currentlySelectedDSQSite = response.LocationDetailsFound;
                    s.displayDSQLocationDetailsPopup = true;
                });
            }
        }, error => { console.log(error); });
    }

    private syncDSQLocation(): void {
        const req = {
            SiteId: this.state.site.Id,
            DSQLocation: this.state.currentlySelectedDSQSite
        };

        Client.PostDSQ(ServerOperations.Server.DSQ_SynchronizeLocation, req, response => {
            if (response.Errors != null && response.Errors.length > 0) {
                sessionStore.showPopup(response.Errors);
            }
            else {
                // Refresh Sites
                if (response.SiteCache != null) {
                    cacheStore.refreshSites(response);

                    let refreshedSites: Site[] = response.SiteCache;
                    let sitesModel: SitesModel[] = [];

                    refreshedSites.forEach(x => sitesModel.push(new SitesModel(x)));

                    if (sitesModel != null && sitesModel.length > 0) {
                        let currentSite = sitesModel.filter(x => x.Model.Id == this.state.site.Id).firstOrDefault();

                        this.mutateState(s => {
                            s.displayDSQLocationDetailsPopup = false;
                            s.displayDSQLocationSearchResultsPopup = false;

                            sessionStore.dispatch(LocalOperations.Sites.OpenSelectedSite, { site: currentSite });
                            sessionStore.dispatch(LocalOperations.Sites.LoadSites);
                        });
                    }
                }
                else {
                    this.mutateState(s => {
                        s.displayDSQLocationDetailsPopup = false;
                        s.displayDSQLocationSearchResultsPopup = false;
                    });
                }
            }
        }, error => { console.log(error); });
    }
}

export { SiteView };
